import {
  WEAPON_CLASS_RESTRICTIONS,
  ARMOR_CLASS_RESTRICTIONS,
  type Equipment,
  type Hero,
  type ArmorSubtype,
} from '@roe/shared';

/** Player-facing label for an armor sub-type. Falls back to the archetype name
 *  for legacy items rolled before the sub-type system landed (no `armorSubtype`). */
const ARMOR_SUBTYPE_LABEL: Record<ArmorSubtype, string> = {
  plate: 'Plate', brigandine: 'Brigandine',
  skirmisher: 'Skirmisher', hunter: 'Hunter',
  robe: 'Robe', vestment: 'Vestment',
};
export function getArmorTypeLabel(item: Equipment): string {
  if (item.armorSubtype) return ARMOR_SUBTYPE_LABEL[item.armorSubtype];
  if (item.armorArchetype) {
    return ({ heavy: 'Heavy', medium: 'Medium', light: 'Light' } as const)[item.armorArchetype];
  }
  return '';
}

/** Player-facing label for a weapon type. Used by weapon tooltips alongside the
 *  flavor name (e.g. "Bonecleaver · Sword") so the player isn't lost when item
 *  names stop containing the type word. */
const WEAPON_TYPE_LABEL: Record<string, string> = {
  sword: 'Sword', sword_2h: '2H Sword',
  axe: 'Axe',
  mace: 'Mace', mace_2h: '2H Mace',
  dagger: 'Dagger',
  staff: 'Staff',
  bow: 'Bow',
  shield: 'Shield',
  shuriken: 'Shuriken',
  spear: 'Spear',
  focus: 'Focus',
  throwing_knife: 'Throwing Knife',
  wand: 'Wand',
};
export function getWeaponTypeLabel(item: Equipment): string {
  if (!item.weaponType) return '';
  return WEAPON_TYPE_LABEL[item.weaponType] ?? item.weaponType.replace(/_/g, ' ');
}

// ─────────────────────────────────────────────────────────────────────────
// Shared item-tooltip stat formatter.
// HeroScene / InventoryScene / BattleScene-merchant all call into this so the
// three tooltips read identically — full stat names, offense-first ordering,
// one stat per line. No base-damage concept in this game; damage lives in
// `statBonuses.attack`, so ATK just sits at the top of the stat list.
// ─────────────────────────────────────────────────────────────────────────

/** Full human-readable stat names — no abbreviations ("Attack" not "ATK",
 *  "Critical Damage" not "CritDmg"). Covers both flat keys (`attack`) and
 *  pct-suffixed affix keys (`atkPct`). */
const FULL_STAT_NAMES: Record<string, string> = {
  attack: 'Attack',
  spellPower: 'Spell Power',
  hp: 'HP',
  mp: 'Mana',
  defense: 'Defense',
  speed: 'Speed',
  critChance: 'Critical Chance',
  critDamage: 'Critical Damage',
  fireResist: 'Fire Resistance',
  frostResist: 'Frost Resistance',
  shadowResist: 'Shadow Resistance',
  natureResist: 'Nature Resistance',
  atkPct: 'Attack',
  spellPowerPct: 'Spell Power',
  hpPct: 'HP',
  defPct: 'Defense',
  maxMpPct: 'Max Mana',
  mpRegenPct: 'Mana Regen',
  critDmgPct: 'Critical Damage',
  attackSpeedPct: 'Attack Speed',
  blockPct: 'Block',
  cdrPct: 'Cooldown Reduction',
  ignoreArmorPct: 'Armor Penetration',
  goldPct: 'Gold Find',
  lootPct: 'Loot Chance',
  xpPct: 'XP Gain',
  vsBeastPct:    'vs Beasts',
  vsDemonPct:    'vs Demons',
  vsUndeadPct:   'vs Undead',
  vsOrcPct:      'vs Orcs',
  vsDragonPct:   'vs Dragons',
  vsMinotaurPct: 'vs Minotaurs',
  vsVoidPct:     'vs Void',
  vsBossPct:     'vs Bosses',
};

/** Canonical order: offensive stats FIRST (Attack / Spell Power), then HP-pool
 *  survivability, defense, crit, utility, faction bonuses, economy, resists. */
const TOOLTIP_STAT_ORDER: string[] = [
  'attack', 'atkPct',
  'spellPower', 'spellPowerPct',
  'hp', 'hpPct',
  'mp', 'maxMpPct', 'mpRegenPct',
  'defense', 'defPct',
  'speed',
  'critChance',
  'critDamage', 'critDmgPct',
  'attackSpeedPct',
  'blockPct',
  'cdrPct',
  'ignoreArmorPct',
  'vsBeastPct', 'vsDemonPct', 'vsUndeadPct', 'vsOrcPct',
  'vsDragonPct', 'vsMinotaurPct', 'vsVoidPct', 'vsBossPct',
  'goldPct', 'lootPct', 'xpPct',
  'fireResist', 'frostResist', 'shadowResist', 'natureResist',
];

function fullStatName(k: string): string {
  if (FULL_STAT_NAMES[k]) return FULL_STAT_NAMES[k];
  if (k.endsWith('Pct')) {
    const root = k.slice(0, -3);
    return root.charAt(0).toUpperCase() + root.slice(1);
  }
  return k;
}

// Stats whose values are ALWAYS expressed as a percentage, regardless of
// magnitude. critDamage is stored as a decimal (0.10 = +10%); without this
// explicit list, a value >= 1.0 (e.g. legendary bow rolling 1.20) fell into
// the flat-integer branch and rendered as "+1" on the tooltip.
const PCT_STATS: ReadonlySet<string> = new Set([
  'critChance', 'critDamage',
]);

// Stats whose stored value is ALREADY in percent units (not a 0-1 fraction).
// attackSpeedPct is the odd one out: loot generator stores e.g. 1.2 to mean 1.2 %
// (matches sigil affix convention, and combat at BattleScene.ts:4083 divides by
// 100 when consuming). Without this exclusion the generic `* 100` formatter
// rendered a legendary Reaper's Coat as "+120% Attack Speed" while combat only
// saw 1.2 %.
const PCT_STATS_STORED_AS_PERCENT: ReadonlySet<string> = new Set([
  'attackSpeedPct',
]);

function formatItemStatValue(k: string, v: number): string {
  if (k.endsWith('Resist')) {
    const sign = v >= 0 ? '+' : '';
    return `${sign}${Math.round(v)}%`;
  }
  if (PCT_STATS_STORED_AS_PERCENT.has(k)) {
    const rounded = Math.abs(v) < 10 ? v.toFixed(1) : v.toFixed(0);
    const sign = v >= 0 ? '+' : '';
    return `${sign}${rounded}%`;
  }
  // Percent stats: explicit list + any key ending in "Pct". Everything else
  // uses the magnitude heuristic: < 1 → pct (affixes), ≥ 1 → flat (hp/atk).
  const asPct = PCT_STATS.has(k) || k.endsWith('Pct') || Math.abs(v) < 1;
  if (asPct) {
    const pct = v * 100;
    const rounded = Math.abs(pct) < 10 ? pct.toFixed(1) : pct.toFixed(0);
    const sign = v >= 0 ? '+' : '';
    return `${sign}${rounded}%`;
  }
  const sign = v >= 0 ? '+' : '';
  return `${sign}${Math.round(v)}`;
}

/** Build the ordered list of "+X Stat" lines for an equipment tooltip.
 *  Callers join with '\n' and render centered inside the tooltip container. */
export function formatEquipmentTooltipLines(item: Equipment): string[] {
  const entries = Object.entries(item.statBonuses).filter(([, v]) => v && v !== 0);
  const orderIdx = (k: string): number => {
    const i = TOOLTIP_STAT_ORDER.indexOf(k);
    return i === -1 ? 9999 : i;
  };
  entries.sort(([a], [b]) => orderIdx(a) - orderIdx(b));
  return entries.map(([k, v]) => `${formatItemStatValue(k, v as number)} ${fullStatName(k)}`);
}

/** Mirror of the canonical equip check used by `GameStateManager.equipItem` —
 *  class restriction + weapon-class restriction + armor-archetype restriction.
 *  Level requirement is intentionally NOT checked here: tooltips want to
 *  show "this hero will be able to equip the gear once they level up" rather
 *  than flipping red just because the hero is currently below the item level. */
export function canHeroEquipItem(hero: Hero, item: Equipment): boolean {
  if (item.classRestriction && item.classRestriction !== hero.heroClass) return false;
  if (item.weaponType && WEAPON_CLASS_RESTRICTIONS[item.weaponType]
      && !WEAPON_CLASS_RESTRICTIONS[item.weaponType].includes(hero.heroClass)) return false;
  if (item.armorArchetype && ARMOR_CLASS_RESTRICTIONS[item.armorArchetype]
      && !ARMOR_CLASS_RESTRICTIONS[item.armorArchetype].includes(hero.heroClass)) return false;
  return true;
}

/** Build the contextual equip-status accent line for an item tooltip.
 *  - If the focused hero (if any) can equip + at least one ally can: lists
 *    every deployed hero who can equip in green.
 *  - If the focused hero can NOT equip: red "Can't equip [armor/weapon kind]"
 *    plus the list of deployed allies who CAN equip (if any), so the player
 *    knows where the item should go instead.
 *  - When no focused hero is provided (e.g. inventory list view), behaves like
 *    "can equip" if any deployed hero can take it, else "no deployed hero can
 *    equip". */
export function buildEquipStatusAccent(
  item: Equipment,
  deployedHeroes: Hero[],
  focusedHero?: Hero,
): { text: string; color: string } {
  const eligible = deployedHeroes.filter((h) => canHeroEquipItem(h, item));
  const eligibleNames = eligible.map((h) => h.name).join(', ');
  const focusedCanEquip = focusedHero ? canHeroEquipItem(focusedHero, item) : eligible.length > 0;

  // Bronze/gold-friendly red + teal from the project palette. Teal (not the
  // uncommon-rarity green `#22c55e`) so the "can equip" line visually
  // separates from the item name when the item is uncommon — previously both
  // read as the same green and the tooltip looked monochromatic.
  const RED = '#ef4444';
  const GREEN = '#5eead4';

  if (focusedCanEquip) {
    if (eligibleNames.length > 0) {
      return { text: `Can equip on: ${eligibleNames}`, color: GREEN };
    }
    return { text: 'Can equip', color: GREEN };
  }
  // Focused hero can't equip — explain why with the gear-kind label, then list
  // the deployed allies who can take it.
  const kindLabel = item.armorArchetype || item.armorSubtype
    ? getArmorTypeLabel(item)
    : getWeaponTypeLabel(item);
  const reason = kindLabel ? `Can't equip ${kindLabel}` : "Can't equip";
  if (eligibleNames.length > 0) {
    return { text: `${reason} — try: ${eligibleNames}`, color: RED };
  }
  return { text: reason, color: RED };
}
